import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useState,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';

import { Theme } from '@app/theme';
import { Input } from '@app/form';
import { FieldChoice, hasCustomSelectChoice } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  input: {
    paddingRight: theme.iconSize.regular * 2,
  },
  deleteBin: {
    display: 'block',
    color: theme.colors.red,
    fontSize: theme.iconSize.regular,
  },
  hidden: {
    display: 'none',
  },
}));

interface Props {
  label?: string;
  autoFocus: boolean;
  isDeletable: boolean;
  placeholder: string;
  choice: FieldChoice;
  onChange: (index: number, choice: FieldChoice) => any;
  onPressEnter: (index: number) => any;
  onRemove: (index: number) => any;
  index: number;
}

export const ChoiceBuilder = ({
  onPressEnter,
  onChange,
  choice,
  onRemove,
  autoFocus,
  isDeletable,
  placeholder,
  index,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  // We have a local state for local Change to prevent too much rendering
  const [value, setValue] = useState(choice.label);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          onPressEnter(index);
          break;
        case 'Backspace':
          if (!event.currentTarget.value && isDeletable) {
            event.preventDefault();
            onRemove(index);
          }
      }
    },
    [onPressEnter, isDeletable, index, onRemove],
  );

  const _onRemove = useCallback(() => onRemove(index), [onRemove, index]);

  const _onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setValue(e.currentTarget.value),
    [setValue],
  );

  const onBlur = useCallback(
    () => onChange(index, { ...choice, label: value }),
    [onChange, choice, value, index],
  );

  const isCustomChoice = hasCustomSelectChoice([choice.ref]);

  return (
    <Input
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      noMargin
      autoFocus={autoFocus}
      className={classes.input}
      onBlur={onBlur}
      hoverRightIcon={
        <i
          className={classnames('ri-delete-bin-line', classes.deleteBin, {
            [classes.hidden]: !isDeletable,
          })}
          onClick={_onRemove}
        />
      }
      onChange={!isCustomChoice ? _onChange : undefined}
      value={value}
      small
    />
  );
};
